import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { Avatar } from '@ublend-npm/aulaui-next';
import { useFlags } from 'launchdarkly-react-client-sdk';

import PageContainer from '$components/PageContainer';
import Error from '$components/Error';
import Spinner from '$components/Spinner';
import List, {
  ListItem,
  ListItemAvatar,
  ListItemContent,
  ListItemPrimaryText,
  ListItemSecondaryText,
  ListItemTertiaryText,
  EmptyListItemText,
} from '$components/List';
import { User, ErrorType } from '$variables/types';
import * as TYPES from '$variables/types';
import { STUDENT } from '$variables/constants';

const StudentPage = ({
  spaceId,
  students,
  fetchParticipantsInSpace,
  error,
  isLoading,
  viewAs,
}) => {
  const participantsNotFetched = !Array.isArray(students);
  const history = useHistory();
  const { ssAstudentIdShowInCsVandSubList } = useFlags();

  const moveToStudentPage =
    ({ studentId }) =>
    () => {
      history.push(`/${spaceId}/students/${studentId}`);
    };

  useEffect(() => {
    if (participantsNotFetched) {
      fetchParticipantsInSpace();
    }
  }, [participantsNotFetched]);

  if (error) {
    return <Error />;
  }

  if (isLoading || participantsNotFetched) {
    return (
      <PageContainer>
        <Spinner />
      </PageContainer>
    );
  }

  return (
    <PageContainer withSideBar={viewAs !== STUDENT}>
      <List testId="student-list">
        {students.length > 0 &&
          students.map((student) => (
            <ListItem
              key={student.id}
              onClick={moveToStudentPage({ studentId: student.id })}
            >
              <ListItemAvatar>
                <Avatar
                  src={student.avatar}
                  size="large"
                  name={student.fullName}
                />
              </ListItemAvatar>
              <ListItemContent>
                <ListItemPrimaryText>{student.fullName}</ListItemPrimaryText>
                <ListItemSecondaryText>{student.email}</ListItemSecondaryText>
                {ssAstudentIdShowInCsVandSubList &&
                  student?.customStudentId && (
                    <ListItemTertiaryText>
                      <strong>Student ID</strong> : {student.customStudentId}
                    </ListItemTertiaryText>
                  )}
              </ListItemContent>
            </ListItem>
          ))}
        {students.length === 0 && (
          <ListItem>
            <EmptyListItemText>
              Seems like there are no students in this space.
            </EmptyListItemText>
          </ListItem>
        )}
      </List>
    </PageContainer>
  );
};

StudentPage.propTypes = {
  spaceId: PropTypes.string.isRequired,
  students: PropTypes.arrayOf(User),
  fetchParticipantsInSpace: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  error: ErrorType,
  viewAs: TYPES.ViewAs,
};

StudentPage.defaultProps = {
  students: undefined,
  isLoading: false,
  error: null,
  viewAs: STUDENT,
};

export default StudentPage;
